import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { fetchAuthSession } from "aws-amplify/auth";
import { withCustomAuthenticator } from "../components/customAuthenticator";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "./Portfolio.css";

const transactionTypes = ["Trade", "Airdrop", "Staking", "FailedTx", "Gas"];

const ITEMS_PER_PAGE = 10;

const EditTransactionModal = ({ transaction, onClose, onSave, isOpen }) => {
  const [editData, setEditData] = useState(transaction);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const session = await fetchAuthSession();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/updateUserTransaction`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.tokens.idToken.toString()}`,
          },
          body: JSON.stringify(editData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update transaction");
      }

      onSave(editData);
      onClose();
    } catch (error) {
      console.error("Error updating transaction:", error);
    }
  };

  const renderEditFields = () => {
    const commonFields = (
      <>
        <div className="form-group date-input">
          <label htmlFor="edit-date">Date</label>
          <input
            type="datetime-local"
            id="edit-date"
            value={editData["timestamp#transactionId"].split("#")[0]}
            disabled
            className="disabled-input"
          />
        </div>

        <div className="form-group transaction-type">
          <label htmlFor="edit-transaction">Type</label>
          <select
            id="edit-transaction"
            value={editData.transaction}
            onChange={(e) =>
              setEditData({
                ...editData,
                transaction: e.target.value,
              })
            }
            required
          >
            {transactionTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </>
    );

    const sentFields = (
      <>
        <div className="form-group asset-input">
          <label htmlFor="edit-assetSent">Asset Sent</label>
          <input
            type="text"
            id="edit-assetSent"
            value={editData.assetSent}
            onChange={(e) =>
              setEditData({
                ...editData,
                assetSent: e.target.value,
              })
            }
            placeholder="BTC"
            required
          />
        </div>

        <div className="form-group number-input">
          <label htmlFor="edit-quantitySent">Qty Sent</label>
          <input
            type="number"
            id="edit-quantitySent"
            value={editData.quantitySent}
            onChange={(e) =>
              setEditData({
                ...editData,
                quantitySent: e.target.value,
              })
            }
            step="any"
            placeholder="0.00"
            required
          />
        </div>
      </>
    );

    const receivedFields = (
      <>
        <div className="form-group asset-input">
          <label htmlFor="edit-assetReceived">Asset Received</label>
          <input
            type="text"
            id="edit-assetReceived"
            value={editData.assetReceived}
            onChange={(e) =>
              setEditData({
                ...editData,
                assetReceived: e.target.value,
              })
            }
            placeholder="ETH"
            required
          />
        </div>

        <div className="form-group number-input">
          <label htmlFor="edit-quantityReceived">Qty Received</label>
          <input
            type="number"
            id="edit-quantityReceived"
            value={editData.quantityReceived}
            onChange={(e) =>
              setEditData({
                ...editData,
                quantityReceived: e.target.value,
              })
            }
            step="any"
            placeholder="0.00"
            required
          />
        </div>
      </>
    );

    const feeFields = (
      <>
        <div className="form-group asset-input">
          <label htmlFor="edit-feeAsset">Fee Asset</label>
          <input
            type="text"
            id="edit-feeAsset"
            value={editData.feeAsset}
            onChange={(e) =>
              setEditData({
                ...editData,
                feeAsset: e.target.value,
              })
            }
            placeholder="BTC"
          />
        </div>

        <div className="form-group number-input">
          <label htmlFor="edit-feeQuantity">Fee Qty</label>
          <input
            type="number"
            id="edit-feeQuantity"
            value={editData.feeQuantity}
            onChange={(e) =>
              setEditData({
                ...editData,
                feeQuantity: e.target.value,
              })
            }
            step="any"
            placeholder="0.00"
          />
        </div>

        <div className="form-group number-input">
          <label htmlFor="edit-feeUsdValue">Fee USD</label>
          <input
            type="number"
            id="edit-feeUsdValue"
            value={editData.feeUsdValue}
            onChange={(e) =>
              setEditData({
                ...editData,
                feeUsdValue: e.target.value,
              })
            }
            step="any"
            placeholder="0.00"
          />
        </div>
      </>
    );

    const usdValueField = (
      <div className="form-group number-input">
        <label htmlFor="edit-usdValue">USD Value</label>
        <input
          type="number"
          id="edit-usdValue"
          value={editData.usdValue}
          onChange={(e) =>
            setEditData({
              ...editData,
              usdValue: e.target.value,
            })
          }
          step="any"
          placeholder="0.00"
          required
        />
      </div>
    );

    switch (editData.transaction) {
      case "Trade":
        return (
          <>
            {commonFields}
            {sentFields}
            {receivedFields}
            {usdValueField}
            {feeFields}
          </>
        );
      case "Airdrop":
      case "Staking":
        return (
          <>
            {commonFields}
            {receivedFields}
            {usdValueField}
          </>
        );
      case "FailedTx":
      case "Gas":
        return (
          <>
            {commonFields}
            {sentFields}
            {usdValueField}
          </>
        );
      default:
        return commonFields;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Edit Transaction</h3>
        <form onSubmit={handleSubmit}>
          <div className="modal-form-grid">{renderEditFields()}</div>

          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-button">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PortfolioSummary = ({ summary, isLoading }) => {
  if (isLoading) {
    return (
      <div className="portfolio-summary-card">
        <h2 className="portfolio-title">Current Holdings</h2>
        <div className="loading-spinner">Loading portfolio summary...</div>
      </div>
    );
  }

  if (
    !summary ||
    !summary.currentHoldings ||
    Object.keys(summary.currentHoldings).length === 0
  ) {
    return (
      <div className="portfolio-summary-card">
        <h2 className="portfolio-title">Current Holdings</h2>
        <div className="no-holdings">No current holdings found</div>
      </div>
    );
  }

  return (
    <div className="portfolio-summary-card">
      <h2 className="portfolio-title">Current Holdings</h2>
      <div className="holdings-grid">
        {Object.entries(summary.currentHoldings).map(([asset, balance]) => (
          <div key={asset} className="holding-item">
            <div className="holding-asset">{asset}</div>
            <div className="holding-balance">{balance}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Portfolio = () => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 16),
    assetSent: "",
    quantitySent: "",
    assetReceived: "",
    quantityReceived: "",
    usdValue: "",
    feeUsdValue: "",
    transaction: "Trade",
    feeAsset: "",
    feeQuantity: "",
  });

  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [transactionError, setTransactionError] = useState("");
  const [transactionSuccess, setTransactionSuccess] = useState("");
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [isLoadingPortfolio, setIsLoadingPortfolio] = useState(true);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");
    setIsSubmitting(true);

    try {
      const session = await fetchAuthSession();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/addUserTransaction`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.tokens.idToken.toString()}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to add transaction");
      }

      setFormSuccess("Transaction added successfully!");
      setPortfolioSummary((prevSummary) => ({
        ...prevSummary,
        currentHoldings: Object.fromEntries(
          Object.entries(prevSummary.currentHoldings).map(
            ([asset, balance]) => {
              if (asset === formData.assetSent) {
                return [
                  asset,
                  parseFloat(balance) - parseFloat(formData.quantitySent),
                ];
              }
              if (asset === formData.assetReceived) {
                return [
                  asset,
                  parseFloat(balance) + parseFloat(formData.quantityReceived),
                ];
              }
              return [asset, balance];
            }
          )
        ),
      }));
      setFormData({
        date: new Date().toISOString().slice(0, 16),
        assetSent: "",
        quantitySent: "",
        assetReceived: "",
        quantityReceived: "",
        usdValue: "",
        feeUsdValue: "",
        transaction: "Trade",
        feeAsset: "",
        feeQuantity: "",
      });
    } catch (err) {
      setFormError(
        err.message || "An error occurred while adding the transaction"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchTransactions = async (lastKey = null) => {
    setIsLoading(true);
    try {
      const session = await fetchAuthSession();
      const queryParams = new URLSearchParams({
        limit: ITEMS_PER_PAGE,
        ...(lastKey && { lastEvaluatedKey: lastKey }),
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/getUserTransactions?${queryParams}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.tokens.idToken.toString()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch transactions");
      }

      const data = await response.json();
      setTransactions((prev) =>
        lastKey ? [...prev, ...data.transactions] : data.transactions
      );
      setLastEvaluatedKey(data.lastEvaluatedKey);
      setHasMore(!!data.lastEvaluatedKey);
    } catch (err) {
      setTransactionError(err.message || "Failed to load transactions");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPortfolioSummary = async () => {
    try {
      const session = await fetchAuthSession();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/getPortfolioSummary`,
        {
          headers: {
            Authorization: `Bearer ${session.tokens.idToken.toString()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch portfolio summary");
      }

      const data = await response.json();
      setPortfolioSummary(data);
    } catch (error) {
      console.error("Error fetching portfolio summary:", error);
    } finally {
      setIsLoadingPortfolio(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchPortfolioSummary();
  }, []);

  const handleLoadMore = () => {
    if (lastEvaluatedKey) {
      fetchTransactions(lastEvaluatedKey);
    }
  };

  const formatDate = (dateString) => {
    const [date] = dateString.split("#");
    return new Date(date).toLocaleString();
  };

  const handleEditClick = (transaction) => {
    setEditingTransaction(transaction);
  };

  const handleEditSave = (updatedTransaction) => {
    setTransactions((prevTransactions) =>
      prevTransactions.map((tx) =>
        tx.date === updatedTransaction.date ? updatedTransaction : tx
      )
    );
    fetchPortfolioSummary();
  };

  const handleRefreshTransactions = () => {
    setTransactionError("");
    setTransactionSuccess("");
    setTransactions([]);
    setLastEvaluatedKey(null);
    setHasMore(true);
    fetchTransactions();
    fetchPortfolioSummary();
  };

  const handleDeleteClick = async (transaction) => {
    if (!window.confirm("Are you sure you want to delete this transaction?")) {
      return;
    }

    try {
      const session = await fetchAuthSession();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/deleteUserTransaction`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.tokens.idToken.toString()}`,
          },
          body: JSON.stringify({
            transactionId: transaction["timestamp#transactionId"],
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete transaction");
      }

      // Remove the transaction from the local state
      setTransactions((prevTransactions) =>
        prevTransactions.filter(
          (tx) =>
            tx["timestamp#transactionId"] !==
            transaction["timestamp#transactionId"]
        )
      );
      setPortfolioSummary((prevSummary) => ({
        ...prevSummary,
        currentHoldings: Object.fromEntries(
          Object.entries(prevSummary.currentHoldings).map(
            ([asset, balance]) => {
              if (asset === transaction.assetSent) {
                return [
                  asset,
                  parseFloat(balance) + parseFloat(transaction.quantitySent),
                ];
              }
              if (asset === transaction.assetReceived) {
                return [
                  asset,
                  parseFloat(balance) -
                    parseFloat(transaction.quantityReceived),
                ];
              }
              return [asset, balance];
            }
          )
        ),
      }));

      setTransactionSuccess("Transaction deleted successfully");
    } catch (error) {
      setTransactionError("Failed to delete transaction");
      console.error("Error deleting transaction:", error);
    }
  };

  const renderFormFields = () => {
    const commonFields = (
      <>
        <div className="form-group date-input">
          <label htmlFor="date">Date</label>
          <input
            type="datetime-local"
            id="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group transaction-type">
          <label htmlFor="transaction">Type</label>
          <select
            id="transaction"
            name="transaction"
            value={formData.transaction}
            onChange={handleInputChange}
            required
          >
            {transactionTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </>
    );

    const sentFields = (
      <>
        <div className="form-group asset-input">
          <label htmlFor="assetSent">Asset Sent</label>
          <input
            type="text"
            id="assetSent"
            name="assetSent"
            value={formData.assetSent}
            onChange={handleInputChange}
            placeholder="BTC"
            required
          />
        </div>

        <div className="form-group number-input">
          <label htmlFor="quantitySent">Qty Sent</label>
          <input
            type="number"
            id="quantitySent"
            name="quantitySent"
            value={formData.quantitySent}
            onChange={handleInputChange}
            step="any"
            placeholder="0.00"
            required
          />
        </div>
      </>
    );

    const receivedFields = (
      <>
        <div className="form-group asset-input">
          <label htmlFor="assetReceived">Asset Received</label>
          <input
            type="text"
            id="assetReceived"
            name="assetReceived"
            value={formData.assetReceived}
            onChange={handleInputChange}
            placeholder="ETH"
            required
          />
        </div>

        <div className="form-group number-input">
          <label htmlFor="quantityReceived">Qty Received</label>
          <input
            type="number"
            id="quantityReceived"
            name="quantityReceived"
            value={formData.quantityReceived}
            onChange={handleInputChange}
            step="any"
            placeholder="0.00"
            required
          />
        </div>
      </>
    );

    const feeFields = (
      <>
        <div className="form-group asset-input">
          <label htmlFor="feeAsset">Fee Asset</label>
          <input
            type="text"
            id="feeAsset"
            name="feeAsset"
            value={formData.feeAsset}
            onChange={handleInputChange}
            placeholder="USD"
          />
        </div>

        <div className="form-group number-input">
          <label htmlFor="feeQuantity">Fee Qty</label>
          <input
            type="number"
            id="feeQuantity"
            name="feeQuantity"
            value={formData.feeQuantity}
            onChange={handleInputChange}
            step="any"
            placeholder="0.00"
          />
        </div>

        <div className="form-group number-input">
          <label htmlFor="feeUsdValue">Fee USD</label>
          <input
            type="number"
            id="feeUsdValue"
            name="feeUsdValue"
            value={formData.feeUsdValue}
            onChange={handleInputChange}
            step="any"
            placeholder="0.00"
          />
        </div>
      </>
    );

    const usdValueField = (
      <div className="form-group number-input">
        <label htmlFor="usdValue">USD Value</label>
        <input
          type="number"
          id="usdValue"
          name="usdValue"
          value={formData.usdValue}
          onChange={handleInputChange}
          step="any"
          placeholder="0.00"
          required
        />
      </div>
    );

    switch (formData.transaction) {
      case "Trade":
        return (
          <>
            {commonFields}
            {sentFields}
            {receivedFields}
            {usdValueField}
            {feeFields}
          </>
        );
      case "Airdrop":
      case "Staking":
        return (
          <>
            {commonFields}
            {receivedFields}
            {usdValueField}
          </>
        );
      case "FailedTx":
      case "Gas":
        return (
          <>
            {commonFields}
            {sentFields}
            {usdValueField}
          </>
        );
      default:
        return commonFields;
    }
  };

  return (
    <div className="portfolio-container">
      <Helmet>
        <title>Coin Basis: Portfolio</title>
        <meta property="og:title" content="Portfolio" />
      </Helmet>
      <Navbar />
      <div className="portfolio-content">
        <div className="portfolio-card" style={{ backgroundColor: '#fff3cd', marginBottom: '2rem', textAlign: 'center', padding: '1rem' }}>
          <p style={{ color: '#856404', margin: 0, fontFamily: 'Inter Tight, sans-serif' }}>
            The portfolio page is currently under development. Portfolio syncing with your uploaded files is coming soon. Stay tuned for updates!
          </p>
        </div>
        <div className="portfolio-card">
          <h2 className="portfolio-title">Add Transaction</h2>
          <form className="transaction-form" onSubmit={handleSubmit}>
            {renderFormFields()}
            <div className="form-group">
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "..." : "Add"}
              </button>
            </div>

            <div className="message-container">
              {formError && <div className="error-message">{formError}</div>}
              {formSuccess && (
                <div className="success-message">{formSuccess}</div>
              )}
            </div>
          </form>
        </div>

        <div className="portfolio-card">
          <PortfolioSummary
            summary={portfolioSummary}
            isLoading={isLoadingPortfolio}
          />
        </div>

        <div className="portfolio-card">
          <div className="history-header">
            <h2 className="portfolio-title">Transaction History</h2>
            <button
              className="refresh-button"
              onClick={handleRefreshTransactions}
              disabled={isLoading}
            >
              {isLoading ? "..." : "↻"}
            </button>
          </div>

          <div className="message-container">
            {transactionError && (
              <div className="error-message">{transactionError}</div>
            )}
            {transactionSuccess && (
              <div className="success-message">{transactionSuccess}</div>
            )}
          </div>

          <div className="transaction-list">
            <table className="transaction-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Asset Sent</th>
                  <th>Qty Sent</th>
                  <th>Asset Received</th>
                  <th>Qty Received</th>
                  <th>USD Value</th>
                  <th>Fee USD</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx["timestamp#transactionId"]}>
                    <td>{formatDate(tx["timestamp#transactionId"])}</td>
                    <td>{tx.transaction}</td>
                    <td>{tx.assetSent}</td>
                    <td>{tx.quantitySent}</td>
                    <td>{tx.assetReceived}</td>
                    <td>{tx.quantityReceived}</td>
                    <td>${parseFloat(tx.usdValue).toFixed(2)}</td>
                    <td>${parseFloat(tx.feeUsdValue || 0).toFixed(2)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="edit-button"
                          onClick={() => handleEditClick(tx)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteClick(tx)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isLoading && <div className="loading-spinner">Loading...</div>}
            {hasMore && !isLoading && (
              <button className="load-more-button" onClick={handleLoadMore}>
                Load More
              </button>
            )}
            {!hasMore && transactions.length > 0 && (
              <div className="no-more-transactions">
                No more transactions to load
              </div>
            )}
            {!isLoading && transactions.length === 0 && (
              <div className="no-transactions">No transactions found</div>
            )}
          </div>
        </div>
      </div>

      {editingTransaction && (
        <EditTransactionModal
          transaction={editingTransaction}
          isOpen={!!editingTransaction}
          onClose={() => setEditingTransaction(null)}
          onSave={handleEditSave}
        />
      )}

      <Footer />
    </div>
  );
};

export default withCustomAuthenticator(Portfolio);
